import * as Sentry from '@sentry/react';
// Safe to read i18n.language synchronously here: index.jsx imports './i18n'
// BEFORE './lib/sentry', and i18next resources are bundled (useSuspense:
// false), so i18n.init() has already resolved the language by the time this
// module runs.
import i18n from '../i18n';

// No-op when VITE_SENTRY_DSN is unset -- every environment without it
// configured is completely unaffected (Phase 5 admin console, Global
// Constraints). Imported once, as a side effect, from index.jsx (must run
// before anything else, same reasoning as the existing i18n side-effect
// import there); other modules (e.g. AuthContext) import the `Sentry`
// export from here to tag events, never call Sentry.init themselves.
if (import.meta.env.VITE_SENTRY_DSN) {
  const tf = (key) => i18n.t(key, { ns: 'common' });
  const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;

  // Sentry's Replay defaults (maskAllText: true, blockAllMedia: true -- the
  // latter blocks every <svg>, which is how Recharts/ReactFlow render their
  // charts) are deliberate everywhere EXCEPT the `test` environment: this app
  // displays customers' business process data (OCEL activity names, KPI
  // values, resource data), so replays must not leak it. But that also masks
  // every chart and label into unreadable placeholders, which made replays
  // useless for reconstructing what a user actually did. `test` carries
  // internal/synthetic traffic rather than the live customer's data, so
  // relax masking there only. Gate on the runtime-resolved value (see
  // frontend/entrypoint.sh) -- the same built image is promoted to
  // production unrebuilt, so this can't branch on a build-time constant.
  const isTestEnvironment = sentryEnvironment === 'test';

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: sentryEnvironment,
    integrations: [
      // Free-tier-friendly by design (see sentry.js's config comment below):
      // 0% ambient sampling, 100% capture on error -- see replaysSessionSampleRate
      // / replaysOnErrorSampleRate.
      Sentry.replayIntegration(
        isTestEnvironment ? { maskAllText: false, blockAllMedia: false } : undefined,
      ),
      Sentry.feedbackIntegration({
        autoInject: true,
        colorScheme: 'light', // app has no dark mode
        // Sentry.setUser() (AuthContext.jsx) already carries the signed-in
        // user's email, so hide the redundant name/email fields and prefill
        // from it instead.
        showName: false,
        showEmail: false,
        useSentryUser: { email: 'email', name: 'email' },
        // Sentry's free-tier terms may require the "Powered by Sentry" mark
        // to stay visible on the widget -- leave true unless the plan is
        // confirmed to allow removing it.
        showBranding: true,
        // Text is resolved once, at init time, in whatever language is
        // active then -- it will NOT reactively relocalize on a later
        // language switch within the same session (would need a page
        // reload, or tearing down/re-adding this integration on
        // i18n's languageChanged event). Acceptable tradeoff for the
        // default auto-injected widget.
        triggerLabel: tf('feedback.triggerLabel'),
        triggerAriaLabel: tf('feedback.triggerAriaLabel'),
        formTitle: tf('feedback.formTitle'),
        submitButtonLabel: tf('feedback.submitButtonLabel'),
        cancelButtonLabel: tf('feedback.cancelButtonLabel'),
        confirmButtonLabel: tf('feedback.confirmButtonLabel'),
        nameLabel: tf('feedback.nameLabel'),
        namePlaceholder: tf('feedback.namePlaceholder'),
        emailLabel: tf('feedback.emailLabel'),
        emailPlaceholder: tf('feedback.emailPlaceholder'),
        messageLabel: tf('feedback.messageLabel'),
        messagePlaceholder: tf('feedback.messagePlaceholder'),
        successMessageText: tf('feedback.successMessageText'),
        isRequiredLabel: tf('feedback.isRequiredLabel'),
        addScreenshotButtonLabel: tf('feedback.addScreenshotButtonLabel'),
        removeScreenshotButtonLabel: tf('feedback.removeScreenshotButtonLabel'),
        errorEmptyMessageText: tf('feedback.errorEmptyMessageText'),
        errorNoClientText: tf('feedback.errorNoClientText'),
        errorTimeoutText: tf('feedback.errorTimeoutText'),
        errorForbiddenText: tf('feedback.errorForbiddenText'),
        errorGenericText: tf('feedback.errorGenericText'),
      }),
    ],
    // Session Replay, free-tier-conscious: never record ordinary sessions,
    // always capture the replay leading up to an error. Maximizes
    // diagnostic value per replay without burning an ambient-sampling quota.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

export default Sentry;
